import { Link, useParams } from "react-router-dom";
import { ArrowLeft, ShieldCheck, Zap, Ban } from "lucide-react";
import { getTool, relatedTools } from "../data/tools";
import { currentLimits } from "../lib/api";
import { ResumirPdf } from "../components/AiResumir";
import { PerguntarPdf } from "../components/AiPerguntar";
import { ToolRunner } from "../components/ToolRunner";
import { ToolCard } from "../components/ToolCard";
import { Seo } from "../components/Seo";
import NotFoundPage from "./NotFoundPage";

const STEPS = [
    "Selecione ou arraste seus arquivos",
    "Ajuste as opções da ferramenta",
    "Clique no botão e baixe o resultado",
];

export default function ToolPage() {
    const { slug } = useParams();
    const tool = getTool(slug);
    if (!tool) return <NotFoundPage />;

    const LIMITS = currentLimits();
    const related = relatedTools(tool);

    return (
        <main className="py-12 px-6 max-w-4xl mx-auto min-h-[60vh]">
            <Seo
                title={`${tool.name} online grátis, sem cadastro — ScorpionPDF`}
                description={`${tool.desc} Ferramenta gratuita e privada: arquivos excluídos automaticamente em até 60 minutos.`}
            />
            <Link
                to="/ferramentas"
                data-testid="back-to-catalog"
                className="inline-flex items-center gap-2 font-mono-tag text-xs font-bold uppercase text-black hover:text-[#0284C7] transition-colors"
            >
                <ArrowLeft className="w-4 h-4" aria-hidden="true" /> Todas as ferramentas
            </Link>

            <div className="mt-6 flex items-start gap-4">
                <div className="w-14 h-14 border-2 border-black bg-[#F0F9FF] flex items-center justify-center flex-shrink-0">
                    <tool.icon className="w-7 h-7 text-black" aria-hidden="true" />
                </div>
                <div>
                    <h1 className="font-poster text-4xl md:text-5xl text-black leading-none">{tool.name}</h1>
                    <p className="text-gray-700 mt-2">{tool.longDesc}</p>
                </div>
            </div>

            <div className="mt-4 flex flex-wrap gap-2 font-mono-tag text-[11px] font-bold uppercase">
                <span className="inline-flex items-center gap-1.5 px-2.5 py-1 bg-[#F0F9FF] border-2 border-black text-black">
                    <ShieldCheck className="w-3.5 h-3.5" aria-hidden="true" /> Exclusão em 60 min
                </span>
                <span className="inline-flex items-center gap-1.5 px-2.5 py-1 bg-[#F0F9FF] border-2 border-black text-black">
                    <Ban className="w-3.5 h-3.5" aria-hidden="true" /> Sem cadastro
                </span>
                <span className="inline-flex items-center gap-1.5 px-2.5 py-1 bg-[#F0F9FF] border-2 border-black text-black">
                    <Zap className="w-3.5 h-3.5" aria-hidden="true" /> Até {LIMITS.maxFileMB} MB · {LIMITS.maxPages} páginas
                </span>
            </div>

            <div className="mt-8">
                {tool.ai === "resumir" ? <ResumirPdf /> : tool.ai === "perguntar" ? <PerguntarPdf /> : <ToolRunner tool={tool} />}
            </div>

            <section className="mt-10" aria-labelledby="como-funciona">
                <h2 id="como-funciona" className="font-poster text-2xl text-black">Como funciona</h2>
                <ol className="mt-4 grid sm:grid-cols-3 gap-4">
                    {STEPS.map((step, i) => (
                        <li key={step} className="border-2 border-black bg-white p-4">
                            <span className="font-poster text-3xl text-[#0284C7]">{String(i + 1).padStart(2, "0")}</span>
                            <p className="text-sm font-medium text-gray-800 mt-1">{step}</p>
                        </li>
                    ))}
                </ol>
            </section>

            {related.length > 0 && (
                <section className="mt-12" aria-labelledby="relacionadas">
                    <h2 id="relacionadas" className="font-poster text-2xl text-black mb-4">Ferramentas relacionadas</h2>
                    <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                        {related.map((t) => (
                            <ToolCard key={t.slug} tool={t} />
                        ))}
                    </div>
                </section>
            )}
        </main>
    );
}
